import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PoweredBy from "../utils/PoweredBy";
import { useTranslation } from "react-i18next";
import BottomNotification from "../components/Notifications/BottomNotifications";
import {
  loginUser,
  fetchUserPermissions,
  getUserProfile,
  setSourceName,
} from "../services/authService";
import ModuleSelect from "../components/ModuleSelect";
import CammyDock from "../components/CammyDock";
import { usePortalConfig } from "../context/PortalConfigContext";
import { Spinner, validateEmail } from "../CommonFunc";
import { Bike, Car, Home, ChevronDown } from "lucide-react";
import { usePageConfig } from "../hooks/usePageConfig";
import { CLIENTS, SELECTABLE_CLIENTS } from "../config/clients";
import { useMediaQuery } from "../hooks/useMediaQuery";
import {
  fetchCredentials,
  fetchQuickLoginOptions,
  isBundledQuickLogin,
  isQuickLoginEnabled,
} from "../config/quickLogin";

const clientRegistry = SELECTABLE_CLIENTS;

// Ergo's property line, in src/config/clients/ergo_property.json.
const PROPERTY_CLIENT_KEY = "ergo_property";

// One dropdown covering client *and* environment. Each file in
// src/config/clients/ is one selectable target: its key is the source_name the
// portal-config API is keyed on, and its `environment` label says which
// deployment it points at.
//
// A second environment for a client is therefore a sibling entry with its own
// suffixed key (e.g. "hegic_uat"), because the portal-config API stores exactly
// one login URL per source_name and so cannot tell environments apart under a
// shared key. Register that key's URL with scripts/manage-login-config.mjs and
// add any host override to CLIENT_API_BASE_URLS in src/config/urls.js.
//
// ergobalticproperty is registered in the client registry only so it can be
// looked up by key elsewhere (host override, media url, etc.) — it is not a
// separately logged-in client, so it never appears as its own dropdown entry.
// Property is reached from the Motor/Property picker after logging in as the
// normal Ergo client, not by selecting a different client here.
const CLIENT_OPTIONS = Object.entries(clientRegistry)
  .filter(([, entry]) => !entry.hiddenFromLoginPicker)
  .map(([key, entry]) => ({
    value: key,
    label: entry.environment
      ? `${entry.label || key} (${entry.environment})`
      : entry.label || key,
  }));

export default function Login() {
  const { pageConfig } = usePageConfig();
  const showClientLogo = pageConfig.branding?.showClientLogo ?? false;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loadPortalConfig } = usePortalConfig();

  const [selectedClient, setSelectedClient] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);
  // Dormant, not deleted: the "Forgot Password" link that set this true was
  // hidden (see KNOWN_DISABLED_FEATURES.md) because the flow it opens
  // doesn't actually reset anything, so nothing in this file can set this
  // true anymore. Left in place rather than torn out along with its trigger,
  // in case the real flow gets wired up later.
  const [showForgetPasswordSection, setShowForgetPasswordSection] =
    useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [permissions, setPermissions] = useState(null);
  // Development only. The list comes from the dev server, because the
  // credentials behind it must never be visible to the bundler — see
  // src/config/quickLogin.js for what happened when they were.
  const showBackgroundVideo = useMediaQuery("(min-width: 640px)");
  const [quickLogin, setQuickLogin] = useState([]);

  useEffect(() => {
    if (!isQuickLoginEnabled()) return undefined;
    let live = true;
    fetchQuickLoginOptions().then((options) => {
      if (live) setQuickLogin(options);
    });
    return () => {
      live = false;
    };
  }, []);

  // Modules the selected client offers after login (Hegic: Break-in/Claims/PAYD).
  // Clients without a `modules` list keep the Motor/Property inspection flow.
  const activeModules = clientRegistry[selectedClient]?.modules || [];

  /**
   * Which inspection lines this client sells, from its own config file. Motor
   * only unless the client says otherwise, which is why Bajaj and Hegic keep
   * the single choice.
   *
   * The login response also carries a per-user `property_flag`, stored in the
   * profile and until now never read. Only an explicit "0" disables the button:
   * a missing flag is not a denial, and treating it as one would silently
   * disable a line the client does sell.
   */
  const inspectionTypes =
    clientRegistry[selectedClient]?.inspectionTypes || ["motor"];
  const clientSellsProperty = inspectionTypes.includes("property");
  const accountHasProperty =
    String(getUserProfile()?.property_flag ?? "") !== "0";
  const propertyEnabled = clientSellsProperty && accountHasProperty;

  const handleReset = () => {
    setSelectedType(null);
  };

  /**
   * One sign-in path.
   *
   * Quick login passes its credentials in; the form passes what was typed.
   * Everything after that — portal config, permissions, the redirect — is the
   * same code, so the shortcut cannot drift away from the real thing.
   */
  const onLogin = async (override) => {
    const client = override?.client || selectedClient;
    const emailToUse = override?.email ?? email;
    const passwordToUse = override?.password ?? password;
    const skipValidation = Boolean(override);

    setIsLoading(true);
    setErrorMessage("");

    if (!client) {
      setErrorMessage("Please select a client before logging in.");
      setIsLoading(false);
      return;
    }

    if (!skipValidation && !validateEmail(emailToUse)) {
      setErrorMessage(t("invalid_email"));
      setIsLoading(false);
      return;
    }

    if (!skipValidation && !passwordToUse.trim()) {
      setErrorMessage(t("password_required") || "Password is required");
      setIsLoading(false);
      return;
    }

    // No client-side login-URL lookup: /api/login-config is an admin route that
    // requires an X-API-KEY, which must never ship in a browser bundle. The
    // portal login (POST /api/login) already resolves the client's login URL
    // server-side from the same record, so asking for it here bought nothing —
    // the call 401'd on every login and we silently used the fallback path.
    // See scripts/manage-login-config.mjs to read or change the registered URL.
    try {
      console.log("[Login] Executing login | client:", client);
      const data = await loginUser(emailToUse, passwordToUse, client);

      // Portal login returns access_token; legacy client login returns status+token
      const loginSucceeded =
        (data?.access_token) || (data?.status === 1 && data?.token);

      if (loginSucceeded) {
        // Phase 3: Load portal config using the UI dropdown value ("bajaj" /
        // "ergo") — the portal config API is keyed on the short UI name, not
        // the backend-canonical client_source_name (e.g. "Bajaj_pp").
        // Which modules this user may see. Only clients that show a module
        // picker have this endpoint — asking on a client without one adds an
        // awaited round-trip to every login for a 404 that nothing reads.
        if (activeModules.length > 0) {
          setPermissions(await fetchUserPermissions(emailToUse, client));
        }

        try {
          console.log("[Login] Loading portal config for:", client);
          await loadPortalConfig(client);
        } catch (configErr) {
          // Portal config failure is non-fatal — log and continue
          console.warn(
            "[Login] Portal config load failed (non-fatal):",
            configErr.message,
          );
        }

        setIsLoading(false);
        setLoggedIn(true);
        setErrorMessage("");
      } else {
        setIsLoading(false);
        if (data?.status === -1) {
          setErrorMessage(
            t("incorrect_email_or_password") || "Incorrect email or password",
          );
        } else {
          setErrorMessage(
            t("login_failed") || "Login failed. Please try again.",
          );
        }
      }
    } catch (err) {
      setIsLoading(false);
      console.error("[Login] Login error:", err);
      setErrorMessage(
        t("something_went_wrong") || "Something went wrong. Please try again.",
      );
    }
  };

  const onClickResetPassword = () => {
    setShowNotification(true);
  };

  const onClickFourWheeler = () => {
    navigate("/redesign/cases");
  };

  /**
   * Property is a separate deployment — ergobalticproperty.dev.api, its own
   * source_name and media host — reached with the same login. Pointing the
   * session at that client entry is all it takes: getApiBaseUrl,
   * getEffectiveSourceName and getMediaBaseUrl all resolve from it, so the
   * ordinary case list then talks to the property backend.
   */
  const onClickProperty = () => {
    setSourceName(PROPERTY_CLIENT_KEY);
    navigate("/redesign/cases");
  };

  return (
    <div className="relative w-full min-h-[100dvh] overflow-x-hidden overflow-y-auto bg-background-dark select-none">
      {/* Background decoration, wide screens only.

          Not `hidden sm:block`: the element would still be in the DOM and iOS
          fetches it anyway. bgVideo.mp4 is 14.3 MB and this page renders a
          video twice — before and after sign-in — so a phone sign-in cost
          about 28 MB before this gate. The dark overlay below already reads
          correctly against bg-background-dark with no video behind it. */}
      {showBackgroundVideo && (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute top-0 left-0 w-full h-full object-cover z-0"
        >
          <source src="/assets/video/bgVideo.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}

      {/* Dark overlay for better contrast */}
      <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-60"></div>

      {/* Main content */}
      {loggedIn ? (
        activeModules.length > 0 ? (
          <ModuleSelect
            modules={activeModules}
            permissions={permissions}
            onSelect={(mod) => navigate(mod.path)}
          />
        ) : (
        <div className="relative w-full min-h-screen overflow-hidden bg-background-dark">
          {/* Same gate as above — this is the second of the two. */}
          {showBackgroundVideo && (
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute top-0 left-0 w-full h-full object-cover"
            >
              <source src="/assets/video/bgVideo.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}

          {/* Dark overlay for better contrast */}
          <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-60"></div>

          {/* Main content - with high z-index */}
          <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
            <div className="max-w-3xl w-full">
              {/* Title */}
              <h1 className="text-xl text-white text-center mb-8 drop-shadow-lg">
                Select Inspection Type
              </h1>

              {/* Initial Selection or Selected Type Display */}
              {!selectedType ? (
                <div className="flex gap-8 justify-center items-center">
                  {/* Motor Button */}
                  <button
                    onClick={() => setSelectedType("motor")}
                    className="group relative w-60 h-16 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-blue-500/50"
                  >
                    <div className="flex items-center justify-center h-full gap-4">
                      <Car className="w-9 h-9 text-white" />
                      <span className="text-xl font-semibold text-white">
                        Motor
                      </span>
                    </div>
                    <div className="absolute inset-0 rounded-2xl bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                  </button>

                  {/* Property — active for clients that sell the line, unless
                      the account's own property_flag says otherwise. Motor has
                      sub-options (2- vs 4-wheeler) and so needs a second step;
                      property has one, so this goes straight through. */}
                  <button
                    type="button"
                    disabled={!propertyEnabled}
                    onClick={onClickProperty}
                    title={
                      clientSellsProperty
                        ? accountHasProperty
                          ? "Property inspections"
                          : "This account has no property access (property_flag 0)"
                        : "This client does not have property inspections"
                    }
                    className={
                      propertyEnabled
                        ? "group relative w-60 h-16 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-blue-500/50"
                        : "relative w-60 h-16 bg-gradient-to-r from-gray-700 to-gray-800 rounded-2xl shadow-xl opacity-50 cursor-not-allowed"
                    }
                  >
                    <div className="flex items-center justify-center h-full gap-4">
                      <Home
                        className={`w-8 h-8 ${
                          propertyEnabled ? "text-white" : "text-gray-400"
                        }`}
                      />
                      <span
                        className={`text-xl font-semibold ${
                          propertyEnabled ? "text-white" : "text-gray-400"
                        }`}
                      >
                        Property
                      </span>
                    </div>
                    {propertyEnabled && (
                      <div className="absolute inset-0 rounded-2xl bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
                    )}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-8">
                  {/* Selected Type Display (Non-clickable, centered) */}
                  <div className="relative px-10 max-w-md h-16 bg-gradient-to-r from-blue-800 to-blue-900 rounded-2xl shadow-2xl">
                    <div className="flex items-center justify-center h-full gap-4">
                      {selectedType === "motor" ? (
                        <>
                          <Car className="w-9 h-9 text-white" />
                          <span className="text-xl font-semibold text-white">
                            Motor
                          </span>
                        </>
                      ) : (
                        <>
                          <Home className="w-8 h-8 text-white" />
                          <span className="text-xl font-semibold text-white">
                            Property
                          </span>
                        </>
                      )}
                    </div>
                    {/* Back button */}
                    <button
                      onClick={handleReset}
                      className="absolute -top-3 -right-3 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full text-white font-bold shadow-lg transition-all duration-200 hover:scale-110"
                    >
                      ×
                    </button>
                  </div>

                  {/* Sub-options */}
                  {selectedType === "motor" && (
                    <div className="w-full flex gap-4 justify-center animate-in fade-in slide-in-from-bottom-4 duration-500">
                      {/* 2 Wheeler Button (Disabled) */}
                      <button
                        disabled
                        className="relative w-64 h-16 bg-gradient-to-r from-gray-700 to-gray-800 rounded-xl shadow-lg opacity-50 cursor-not-allowed"
                      >
                        <div className="flex items-center justify-center h-full gap-3">
                          <Bike className="w-8 h-8 text-gray-400" />
                          <span className="text-xl font-semibold text-gray-400">
                            2 Wheeler
                          </span>
                        </div>
                      </button>

                      {/* 4 Wheeler Button */}
                      <button
                        onClick={onClickFourWheeler}
                        className="group relative w-64 h-16 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 rounded-xl shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-green-500/50"
                      >
                        <div className="flex items-center justify-center h-full gap-3">
                          <Car className="w-8 h-8 text-white" />
                          <span className="text-xl font-semibold text-white">
                            4 Wheeler
                          </span>
                        </div>
                        <div className="absolute inset-0 rounded-xl bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Parkhi here too, same reasoning as ModuleSelect.js — this
              "Select Inspection Type" screen (Ergo/Bajaj's post-login step)
              sits outside Layout just like Hegic's module picker does, so
              without this he'd stay invisible through this entire screen. */}
          <div className="hidden lg:block">
            <CammyDock glow />
          </div>
        </div>
        )
      ) : (
        <div className="relative z-10 h-full w-full">
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 flex justify-between items-center p-8">
            {/* Client Logo */}
            {showClientLogo && (
              <div className="text-[#ff4f6d] text-2xl font-bold tracking-wider">
                ERGO
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="relative z-10 flex min-h-full flex-col justify-center pt-24 pb-8 box-border lg:flex-row lg:items-center">
            {/* Left Side - Hero Text */}
            <div className="hidden lg:block lg:flex-1 lg:pl-16 lg:pr-8">
              <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-8">
                Discover AI-driven visual
                <br />
                inspection like never before
              </h1>
            </div>

            {/* Right Side - Login Form */}
            <div className="w-full px-6 lg:w-96 lg:px-0 lg:pr-16">
              <div className="w-full max-w-md inline-flex flex-col justify-start items-center gap-5">
                {showForgetPasswordSection ? (
                  <>
                    <div className="w-full text-white">
                      <h1 className=" text-2xl ">{t("forgot_password")}</h1>
                      <p className="text-base">{t("forgot_password_text")}</p>

                      <div className="self-stretch w-full bg-background-secondary hover:bg-background-primary mt-8 h-12 px-5 py-3.5 rounded-2xl backdrop-blur-blur inline-flex justify-start items-center gap-2">
                        <label
                          className="w-16 justify-end text-black text-xs font-medium font-noto-sans"
                          htmlFor="email-input"
                        >
                          {t("email")}
                        </label>
                        <input
                          id="email-input"
                          type="email"
                          className="flex-1  border-none outline-none text-black text-base font-normal font-noto-sans placeholder:text-gray-400"
                          placeholder="Enter your email"
                          value={forgotPasswordEmail}
                          onChange={(e) =>
                            setForgotPasswordEmail(e.target.value)
                          }
                          autoComplete="username"
                        />
                      </div>
                      <button
                        type="button"
                        className="self-stretch w-full h-12 px-5 disabled:opacity-70 disabled:hover:bg-primary py-3.5 mt-4 bg-primary hover:bg-blue-700 transition-colors duration-200 rounded-2xl flex justify-center items-center text-white text-base font-semibold font-noto-sans border border-blue-700 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
                        style={{ width: "100%" }}
                        onClick={onClickResetPassword}
                        disabled={!forgotPasswordEmail}
                      >
                        {t("reset_password")}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                        >
                          <path
                            d="M13.75 7.25L19.25 12.5L13.75 17.75"
                            stroke="white"
                            strokeWidth="2.5"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M19 13.75C19.6904 13.75 20.25 13.1904 20.25 12.5C20.25 11.8096 19.6904 11.25 19 11.25V13.75ZM19 11.25H4.75V13.75H19V11.25Z"
                            fill="white"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="self-stretch px-5 pt-5 bg-transparent duration-200 rounded-2xl flex justify-center items-center text-white text-xs font-noto-sans hover:text-blue-600"
                        style={{ width: "100%" }}
                        onClick={() => setShowForgetPasswordSection(false)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 25"
                          className="w-5 h-5 mr-2"
                          fill="none"
                        >
                          <path
                            d="M10.25 7.25L4.75 12.5L10.25 17.75"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M5 13.75C4.30964 13.75 3.75 13.1904 3.75 12.5C3.75 11.8096 4.30964 11.25 5 11.25V13.75ZM5 11.25H19.25V13.75H5V11.25Z"
                            fill="currentColor"
                          />
                        </svg>
                        {t("back")}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="self-stretch flex flex-col justify-start items-start gap-4">
                      {/* ── Phase 1: Client Selector ── */}
                      <div className="self-stretch h-12 px-5 py-3.5 bg-text-primary border border-border-input rounded-lg inline-flex justify-start items-center gap-2 relative">
                        <label
                          className="w-16 justify-end text-text-secondary text-xs font-medium font-noto-sans shrink-0"
                          htmlFor="client-select"
                        >
                          CLIENT
                        </label>
                        <select
                          id="client-select"
                          className="flex-1 bg-transparent border-none outline-none text-base font-normal font-noto-sans cursor-pointer appearance-none pr-6"
                          style={{
                            WebkitBoxShadow: "0 0 0 1000px #1e2125 inset",
                            WebkitTextFillColor: selectedClient
                              ? "white"
                              : "#9ca3af",
                            colorScheme: "dark",
                          }}
                          value={selectedClient || ""}
                          onChange={(e) => {
                            setSelectedClient(e.target.value || null);
                            setErrorMessage("");
                          }}
                        >
                          <option
                            value=""
                            disabled
                            style={{
                              backgroundColor: "#1e2125",
                              color: "#9ca3af",
                            }}
                          >
                            Select Client
                          </option>
                          {CLIENT_OPTIONS.map((opt) => (
                            <option
                              key={opt.value}
                              value={opt.value}
                              style={{
                                backgroundColor: "#1e2125",
                                color: "white",
                              }}
                            >
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {/* Custom chevron so the select arrow matches the dark theme */}
                        <ChevronDown
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                        />
                      </div>

                      {/* Email Input – disabled until client is selected */}
                      <div
                        className={`self-stretch h-12 px-5 py-3.5 bg-text-primary border border-border-input rounded-lg inline-flex justify-start items-center gap-2 transition-opacity duration-200 ${
                          !selectedClient ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <label
                          className="w-16 justify-end text-text-secondary text-xs font-medium font-noto-sans"
                          htmlFor="email-input"
                        >
                          EMAIL ID
                        </label>
                        <input
                          id="email-input"
                          type="email"
                          disabled={!selectedClient}
                          className="flex-1 bg-transparent border-none outline-none text-white text-base font-normal font-noto-sans placeholder:text-gray-600 focus:bg-transparent active:bg-transparent disabled:cursor-not-allowed"
                          style={{
                            WebkitBoxShadow: "0 0 0 1000px #1e2125 inset",
                            WebkitTextFillColor: "white",
                            backgroundColor: "transparent !important",
                          }}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          autoComplete="username"
                        />
                      </div>

                      {/* Password Input – disabled until client is selected */}
                      <div
                        className={`self-stretch h-12 px-5 py-3.5 bg-text-primary border border-border-input rounded-lg inline-flex justify-start items-center gap-2 relative transition-opacity duration-200 ${
                          !selectedClient ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <label
                          className="w-16 justify-end text-text-secondary text-xs font-medium font-noto-sans"
                          htmlFor="password-input"
                        >
                          PASSWORD
                        </label>
                        <input
                          id="password-input"
                          type={showPassword ? "text" : "password"}
                          disabled={!selectedClient}
                          className="flex-1 bg-transparent border-none outline-none text-white text-base font-normal font-noto-sans focus:bg-transparent active:bg-transparent disabled:cursor-not-allowed"
                          style={{
                            WebkitBoxShadow: "0 0 0 1000px #1e2125 inset",
                            WebkitTextFillColor: "white",
                            backgroundColor: "transparent !important",
                          }}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          className="text-primary hover:text-primary-hover absolute right-2"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1.66667 10C1.66667 10 4.16667 4.16667 10 4.16667C15.8333 4.16667 18.3333 10 18.3333 10C18.3333 10 15.8333 15.8333 10 15.8333C4.16667 15.8333 1.66667 10 1.66667 10Z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1.66667 10C1.66667 10 4.16667 4.16667 10 4.16667C15.8333 4.16667 18.3333 10 18.3333 10C18.3333 10 15.8333 15.8333 10 15.8333C4.16667 15.8333 1.66667 10 1.66667 10Z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M3.33334 3.33334L16.6667 16.6667"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    {errorMessage && (
                      <BottomNotification
                        message={errorMessage}
                        color={"bg-red-500"}
                        setCloseNotification={setErrorMessage}
                      />
                    )}

                    {/* One-click sign-in. In development the credentials
                        come from the dev server and never enter the bundle. A
                        build made with REACT_APP_QUICK_LOGIN=on carries them
                        INSIDE main.js, readable by anyone who loads this page
                        — the label below says so on purpose, so an operator
                        looking at a deploy can tell. See QUICK_LOGIN.md. */}
                    {isQuickLoginEnabled() && quickLogin.length > 0 && (
                      <div className="self-stretch mt-4 rounded-lg border border-dashed border-gray-600 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-300 text-xs font-semibold font-noto-sans uppercase tracking-wider">
                            Quick sign-in
                          </span>
                          <span className="text-gray-500 text-[10px] font-noto-sans">
                            {isBundledQuickLogin()
                              ? "credentials are public in this build"
                              : "development only"}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {quickLogin.map((option) => (
                            <button
                              key={option.key}
                              type="button"
                              disabled={!option.ready || isLoading}
                              title={
                                option.ready
                                  ? `Sign in as ${option.email}`
                                  : `Set ${option.envVars.join(" and ")}`
                              }
                              onClick={async () => {
                                setSelectedClient(option.key);
                                setIsLoading(true);
                                // Fetched at the click, so the password lives
                                // no longer than the sign-in itself.
                                const credentials = await fetchCredentials(option.key);
                                if (!credentials) {
                                  setIsLoading(false);
                                  setErrorMessage(
                                    `No credentials configured for ${option.label}. Set ${option.envVars.join(" and ")}${isBundledQuickLogin() ? " as build arguments and rebuild." : " in .env.local and restart the dev server."}`,
                                  );
                                  return;
                                }
                                onLogin({ ...credentials, client: option.key });
                              }}
                              className="flex-1 min-w-[92px] rounded-lg border border-gray-600 px-3 py-2 text-sm font-semibold font-noto-sans text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              {option.label}
                              <span className="block text-[10px] font-normal text-gray-400">
                                {option.ready ? option.environment : "not configured"}
                              </span>
                            </button>
                          ))}
                        </div>
                        {quickLogin.every((option) => !option.ready) && (
                          <p className="mt-2 text-[11px] leading-snug text-gray-400 font-noto-sans">
                            {isBundledQuickLogin()
                              ? "Pass the REACT_APP_QUICK_LOGIN_* values as build arguments and rebuild the image."
                              : "Add the e-mail and password for each client to .env.local, then restart the dev server. The file is gitignored, so nothing is committed."}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Login Button – disabled until client + credentials filled */}
                    <button
                      type="button"
                      className="self-stretch h-12 px-5 py-3.5 mt-4 disabled:bg-gray-600 bg-white hover:bg-gray-100 transition-colors duration-200 rounded-lg flex justify-center items-center text-black text-base font-semibold font-noto-sans shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
                      style={{ width: "100%" }}
                      disabled={!selectedClient || !email || !password}
                      /* Wrapped: onClick would otherwise hand the click
                         event in as the override, and Boolean(event) would
                         switch off the form's own validation. */
                      onClick={() => onLogin()}
                    >
                      LOGIN
                      {isLoading && (
                        <span className="ms-2">
                          <Spinner />
                        </span>
                      )}
                    </button>

                  </>
                )}

                {/* Forgot Password, Google sign-in, and Microsoft sign-in
                    were here and are hidden — none of the three actually
                    work yet. See KNOWN_DISABLED_FEATURES.md before
                    re-enabling any of them. */}
              </div>
            </div>
          </div>

          {/* Footer - Powered by CAMCOM.
              Fixed to the viewport rather than absolute to this panel: an
              absolute position here is measured against this panel's own
              height, which grows with its content (the login form is tall
              enough on its own to exceed a short window before any of this
              badge is counted), and on a shorter Windows display that pushed
              the badge below the fold instead of pinning it in view. */}
          <div className="fixed bottom-8 left-16 z-20">
            <PoweredBy />
          </div>
        </div>
      )}

      {showNotification && (
        <BottomNotification
          message={t("notification.password_sent")}
          setCloseNotification={setShowNotification}
        />
      )}
    </div>
  );
}
